import React, { useState, useEffect, useRef, forwardRef } from "react";
import {
  Box,
  FormHelperText,
  Grid2 as Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import { NewOffsetInput as OffsetInput } from "./NewOffsetInput";
import { cssVar } from "../../theme/theme";
import Heading from "./Heading";
import Button from "./Button";
import { INote } from "./NotesInputRow";
import { CommonFunctions } from "../utils/CommonFunctions";
import { UserRoles, UserRolesInfo } from "../constants/enums";
import { DateFormatter } from "../utils/DateFormatter";
import LinkButton from "./LinkButton";
import { Constants, HtmlHelpers } from "../constants/all-constant";
import Input from "./Input";

const NotesViewUpdateRow = ({
  isCurrentUserStudentOrTutor,
  note,
  handleDeleteNote,
  handleUpdateNote,
  currentEditingNoteId,
  handleEditNote,
  userRole,
}: {
  isCurrentUserStudentOrTutor: boolean;
  note: INote;
  handleDeleteNote(id: number): void;
  handleUpdateNote(
    id: number,
    text: string,
    offsetInMin: number | undefined
  ): void;
  currentEditingNoteId: number | null;
  handleEditNote(id: number | null): void;
  userRole?: any;
}) => {
  const [minRows, setMinRows] = useState(0);
  const [noteText, setNoteText] = useState(note.notesText);
  const [offsetInMinutes, setOffsetInMinutes] = useState<number | undefined>(
    note.offset === 0 ? undefined : note.offset
  );
  const [offsetError, setOffsetError] = useState<string>("");
  const isInEditMode = note.id === currentEditingNoteId;
  const offsetErrorText = "Please enter valid input.";

  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (ref.current?.clientHeight) {
      setMinRows(
        Math.floor(ref.current.clientHeight / 18) < 1
          ? 1 //because 1 line is not going with offset height
          : Math.floor(ref.current.clientHeight / 18)
      );
    }
  }, [setMinRows]);

  const handleUpdateClick = () => {
    if (!noteText) return;
    if (!isCurrentUserStudentOrTutor) {
      if (offsetInMinutes === 0) {
        setOffsetError(offsetErrorText);
        return;
      }
    }

    handleUpdateNote(note.id, noteText, offsetInMinutes);
    setNoteText("");
    setOffsetError("");
    handleEditNote(null);
  };

  return (
    <Grid container spacing={1} key={note.id} sx={{ padding: "10px 0px" }}>
      <Grid size={1.65}>
        {!isCurrentUserStudentOrTutor ? (
          isInEditMode ? (
            <OffsetInput
              onValueChanged={(e) => {
                setOffsetError("");
                setOffsetInMinutes(e);
              }}
              value={note.offset}
              errorMessage={offsetError}
            />
          ) : (
            <React.Fragment>
              <Typography
                className="text-ellipsis"
                style={{
                  marginLeft: "20px",
                  display: "flex",
                  alignItems: "center",
                  fontSize: "1.25rem",
                }}
              >
                Offset
                {/* <Tooltip title="Use the format: 2D 5H, D= Days H= Hours">
                  <span
                    className="material-symbols-outlined"
                    style={{
                      fontSize: "1.2rem",
                      verticalAlign: "text-top",
                      color: cssVar("--color-info-main"),
                      marginLeft: "8px",
                    }}
                  >
                    info
                  </span>
                </Tooltip> */}
              </Typography>
              <Heading
                text={
                  note.offset === 0
                    ? Constants.DEFAULT_OFFSET_VALUE
                    : `${CommonFunctions.convertMinutesToOffset(
                        Number(note.offset)
                      )}`
                }
                type={"body1"}
                classes="text-ellipsis"
                isBold={true}
                style={{ marginLeft: "20px" }}
              />
            </React.Fragment>
          )
        ) : (
          <React.Fragment>
            <Heading
              text={CommonFunctions.formatFirstAndLastName(
                note.firstName,
                note.lastName
              )}
              type={"body1"}
              classes="text-ellipsis"
              isBold={true}
            />
            <Heading
              color="secondary.contrastText"
              text={
                note.roleId === UserRoles.SUPER_USER
                  ? "RN"
                  : UserRolesInfo[note.roleId as UserRoles]?.label
              }
              type={"subtitle2"}
              isBold={true}
            />
            <Heading
              text={DateFormatter.formatTimeWithoutMeridiem(note.createdOn)}
              type={"subtitle2"}
              classes="text-ellipsis"
            />
          </React.Fragment>
        )}
      </Grid>
      <Grid
        size={10.35}
        sx={{
          height: "fit-content",
        }}
      >
        {isInEditMode ? (
          <Input
            minRows={minRows}
            maxRows={minRows}
            multiline={minRows > 1 ? true : false}
            name="noteText"
            label=""
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            style={{
              fontSize: "0.87rem",
              lineHeight: "1.5",
              borderColor: noteText ? "#000000" : "#B1B1B1",
            }}
          />
        ) : (
          <div
            style={{
              padding: "10px",
              backgroundColor: "#EDEFF2",
              borderRadius: "5px",
              height: "fit-content",
            }}
          >
            <Heading
              key={note.id}
              ref={ref}
              text={note.notesText}
              type={"body1"}
              classes="setHtml"
            />
          </div>
        )}

        {!isCurrentUserStudentOrTutor &&
          (userRole && isInEditMode ? (
            <Box className="d-flex" style={{ marginTop: "5px" }}>
              <Button
                text="Cancel"
                type="button"
                variant="outlined"
                size="small"
                className="tinyBtn"
                fullWidth={false}
                isBold={true}
                onClick={() => {
                  handleEditNote(null);
                  setNoteText(note.notesText);
                }}
              />
              <Button
                text="Update"
                type="button"
                className="primary-bg-color ml-1 tinyBtn"
                size="small"
                fullWidth={false}
                isBold={true}
                onClick={handleUpdateClick}
              />
            </Box>
          ) : (
            userRole && (
              <div
                style={{
                  fontSize: "0.75rem",
                  marginTop: "0px",
                  fontWeight: "bold",
                }}
              >
                <LinkButton
                  onClick={() => handleEditNote(note.id)}
                  color={cssVar("--color-black")}
                  text="Edit"
                  style={{ fontSize: "0.75rem" }}
                />
                {HtmlHelpers.FOUR_TIMES_SPACE}
                <LinkButton
                  onClick={() => handleDeleteNote(note.id)}
                  color={cssVar("--color-black")}
                  text="Delete"
                  style={{ fontSize: "0.75rem" }}
                />
              </div>
            )
          ))}
      </Grid>
    </Grid>
  );
};

export default NotesViewUpdateRow;
